// src/components/FeedbackWidget.js
//
// Botón flotante de feedback, visible en toda la plataforma autenticada (montado desde
// Layout.js, excluido de /admin/*). Abre un modal sin navegar fuera de la página con dos
// vistas: "Reportar" (FeedbackForm, el mismo formulario que /reportar/) y "Mis reportes"
// (status + nota corta del admin de las entradas propias, vía getMyDevInboxEntries()).
//
// No es un hilo de conversación — a propósito, ver project-log/initiatives/
// feedback-nombre-newsletter-qr.md: solo un estado simple (nuevo/visto/resuelto) con una
// nota corta opcional, nunca un chat.
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "./AuthProvider";
import { useLanguage } from "./LanguageProvider";
import { getMyDevInboxEntries } from "../services/admin-service";
import FeedbackForm from "./FeedbackForm";

const FeedbackIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.6} stroke="currentColor" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm3.75 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm3.75 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
  </svg>
);

const STATUS_DOT = {
  nuevo: "#C79A3B", // gold-quiet — llegó, sin revisar
  visto: "#7A8B7F", // sage/moss — en revisión
  resuelto: "#2D5A42", // forest — atendido
};

function formatDate(timestamp, language) {
  if (!timestamp?.toDate) return "";
  try {
    return new Intl.DateTimeFormat(language === "en" ? "en-US" : "es-MX", {
      day: "numeric",
      month: "short",
    }).format(timestamp.toDate());
  } catch {
    return "";
  }
}

function MyReportsList({ entries, loading, t, language }) {
  if (loading) {
    return <p className="text-sm text-brand-sage dark:text-brand-parchment/50 py-6 text-center">{t("feedbackWidget.loading")}</p>;
  }
  if (entries.length === 0) {
    return <p className="text-sm text-brand-sage dark:text-brand-parchment/50 py-6 text-center">{t("feedbackWidget.myReportsEmpty")}</p>;
  }
  return (
    <ul className="space-y-3 max-h-[55vh] overflow-y-auto pr-1">
      {entries.map((entry) => {
        const status = entry.status || "nuevo";
        return (
          <li key={entry.id} className="bg-white dark:bg-brand-dark-card rounded-xl border border-brand-gold/20 dark:border-brand-forest p-3.5">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-xs font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-brand-parchment dark:bg-brand-forest/40 text-brand-forest dark:text-brand-parchment">
                {t(`report.type_${entry.type || "idea"}`)}
              </span>
              <span className="text-xs text-brand-haze dark:text-brand-parchment/40">{formatDate(entry.createdAt, language)}</span>
              <span className="ml-auto inline-flex items-center gap-1.5 text-xs font-semibold text-brand-forest dark:text-brand-parchment">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: STATUS_DOT[status] || STATUS_DOT.nuevo }} />
                {t(`feedbackWidget.status_${status}`)}
              </span>
            </div>
            <p className="text-sm text-brand-forest dark:text-brand-parchment/90 whitespace-pre-wrap">{entry.text}</p>
            {entry.adminNote && (
              <p className="mt-2 text-sm text-brand-sage dark:text-brand-parchment/70 border-t border-brand-gold/10 dark:border-brand-forest pt-2">
                <span className="font-semibold">{t("feedbackWidget.adminNoteLabel")}:</span> {entry.adminNote}
              </p>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default function FeedbackWidget() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("new");
  const [myEntries, setMyEntries] = useState([]);
  const [loadingMine, setLoadingMine] = useState(false);
  const [fetchedMine, setFetchedMine] = useState(false);

  const loadMine = useCallback(async () => {
    if (!user?.uid) return;
    setLoadingMine(true);
    try {
      const entries = await getMyDevInboxEntries(user.uid);
      setMyEntries(entries);
      setFetchedMine(true);
    } catch {
      // La regla nueva de firestore.rules (lectura por dueño) todavía puede no estar
      // desplegada — falla en silencio, la lista queda vacía en vez de romper el widget.
    } finally {
      setLoadingMine(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (open && tab === "mine" && !fetchedMine) {
      loadMine();
    }
  }, [open, tab, fetchedMine, loadMine]);

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t("feedbackWidget.openAria")}
        className="fixed bottom-24 md:bottom-6 right-4 md:right-6 z-40 w-12 h-12 rounded-full bg-brand-forest hover:bg-brand-dark dark:bg-brand-forest-light dark:hover:bg-brand-forest text-brand-parchment shadow-lg flex items-center justify-center transition-colors print:hidden"
      >
        <FeedbackIcon className="w-6 h-6" />
      </button>

      {open && (
        <div
          className="fixed inset-0 bg-black/50 z-[60] flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div
            className="bg-brand-parchment dark:bg-brand-dark rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:w-[90vw] sm:max-w-md max-h-[85vh] overflow-y-auto p-5 animate-fade-in-up"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-brand-forest dark:text-brand-parchment">{t("feedbackWidget.title")}</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t("close")}
                className="text-brand-sage dark:text-brand-parchment/60 hover:text-brand-forest dark:hover:text-brand-parchment text-xl leading-none px-1"
              >
                &times;
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                type="button"
                onClick={() => setTab("new")}
                className={`flex-1 text-sm font-semibold px-3 py-1.5 rounded-full transition-colors ${
                  tab === "new"
                    ? "bg-brand-forest dark:bg-brand-gold text-brand-parchment dark:text-brand-forest"
                    : "bg-white dark:bg-brand-dark-card text-brand-forest dark:text-brand-parchment border border-brand-gold/20 dark:border-brand-forest"
                }`}
              >
                {t("feedbackWidget.tabNew")}
              </button>
              <button
                type="button"
                onClick={() => setTab("mine")}
                className={`flex-1 text-sm font-semibold px-3 py-1.5 rounded-full transition-colors ${
                  tab === "mine"
                    ? "bg-brand-forest dark:bg-brand-gold text-brand-parchment dark:text-brand-forest"
                    : "bg-white dark:bg-brand-dark-card text-brand-forest dark:text-brand-parchment border border-brand-gold/20 dark:border-brand-forest"
                }`}
              >
                {t("feedbackWidget.tabMine")}
              </button>
            </div>

            {tab === "new" ? (
              <FeedbackForm onSubmitted={() => setFetchedMine(false)} />
            ) : (
              <MyReportsList entries={myEntries} loading={loadingMine} t={t} language={language} />
            )}
          </div>
        </div>
      )}
    </>
  );
}
